/**
 * BONE WATER — LIQUID BUTTONS
 * assets/bw-liquid-btn.js
 *
 * Puts a flat pool of liquid inside a button. It drifts on its own, under a
 * pointer it is pulled up toward the cursor and settles back afterwards, and
 * a press fills the capsule in a rush before it drains back to its level.
 *
 * Opt-in per button: any element carrying data-bw-liquid is picked up and
 * nothing else on the page is touched. Colours come from the element's own
 * custom properties (--bw-liquid, --bw-liquid-ink), so the section decides
 * them in Liquid and this file never names a brand colour.
 *
 * WHY A SIMULATION AND NOT KEYFRAMES
 * The surface is a row of 26 sprung columns coupled to their neighbours — a
 * one-dimensional pool. A pull at one point therefore travels outward and
 * reflects off the ends, so no two hovers produce the same shape and the
 * return is damping rather than an animation played backwards. The idle drift
 * is the same columns driven by two sine waves whose periods do not divide
 * into each other, which is what stops it visibly looping.
 *
 * WHAT IT COSTS
 * The maths is nothing (26 columns, a few hundred flops a frame). The cost is
 * re-rasterising the button, so that is what gets rationed:
 *   - offscreen buttons are not stepped at all (IntersectionObserver)
 *   - an idle button repaints at 20fps, not 60 — its motion is slow enough
 *     that the difference cannot be seen
 *   - only the button under the pointer runs at full rate
 *   - the label copy is only repainted while liquid actually reaches it
 *   - paint containment keeps the invalidation inside the button box
 * Reduced motion gets one static pour and no loop at all.
 *
 * Presentation lives in bw-tokens.css (.bw-liquid, .bw-liquid__ghost).
 * Requires: assets/bw-tokens.css
 */
(function () {
  /* Emitted by every section that owns a liquid button, so it can arrive more
     than once on a page. Guard rather than fight it — a second copy would
     mount a second pool inside each button. */
  if (window.__bwLiquidBound) return;
  window.__bwLiquidBound = true;

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var fine = window.matchMedia('(hover: hover) and (pointer: fine)');

  /* Columns across the surface. 26 puts a control point every ~10px on a
     260px button, which is finer than the smallest ripple the spread pass can
     carry — more points would cost paint without adding shape. */
  var COLS = 26;

  /* The sim always advances in fixed 1/60 steps regardless of how often we
     paint. Variable dt in a spring system changes its stiffness with the
     frame rate, which is how one pull ends up feeling different on a 120Hz
     laptop and a throttled phone. */
  var STEP = 1 / 60;
  var MAX_STEPS = 4; /* after a stall, catch up four steps and drop the rest */

  var REST_K = 26; /* pull back toward the resting line */
  var DAMP = 3.1; /* velocity decay — low enough to leave ripples behind */
  var SPREAD = 0.24; /* how strongly a column drags its neighbours */
  var PULL_K = 30; /* how hard the cursor lifts the surface under it */
  var IDLE_MS = 50; /* 20fps while nobody is hovering */

  /* The press. On pointerdown (mouse, pen or finger — this one is not
     hover-gated) the surface is driven straight up to the brim over a few
     frames and held there for FILL_S, then released to the resting spring,
     which drains it back in the usual damped return. A link that navigates
     never gets to the drain, which is the point: the fill is the button
     acknowledging the tap before the page goes. */
  var FILL_S = 0.5; /* seconds held at the brim */
  var FILL_RATE = 0.28; /* fraction of the remaining rise taken per step */

  var pools = [];
  var running = false;
  var last = 0;

  function rand(a, b) { return a + Math.random() * (b - a); }
  function clamp(n, lo, hi) { return n < lo ? lo : n > hi ? hi : n; }
  /* A missing or unparseable attribute falls back; a parsed 0 does not. */
  function num(v, dflt) { var n = parseFloat(v); return isNaN(n) ? dflt : n; }

  /* ---------------------------------------------------------------------- */

  function Pool(el) {
    this.el = el;
    this.w = 0;
    this.h = 0;
    this.visible = false;
    this.hidden = false;
    this.hover = false;
    this.px = 0; /* pointer, in the button's own pixels */
    this.py = 0;
    this.t = rand(0, 1000); /* every button starts elsewhere in the drift */
    this.acc = 0;
    this.painted = 0;
    this.ghostLit = true;
    this.fillT = 0; /* seconds of press-fill still to hold */

    /* Resting depth and drift amplitude as fractions of the button height, so
       a small button and a tall one hold the same-looking pour. */
    /* num(), not `|| default`: every one of these settings offers 0 as a
       meaningful value — a drift of 0 is documented in the editor as "holds it
       flat until a cursor arrives" — and 0 is falsy, so the || form silently
       replaced it with the default and the setting did nothing. */
    this.level = clamp(num(el.getAttribute('data-liquid-level'), 22), 4, 92) / 100;
    this.amp = clamp(num(el.getAttribute('data-liquid-motion'), 100), 0, 300) / 100;

    /* Phases, so two buttons on one screen are never in step. */
    this.p1 = rand(0, 6.283);
    this.p2 = rand(0, 6.283);

    this.y = new Float32Array(COLS);
    this.v = new Float32Array(COLS);

    this.mount();
    this.measure();
    this.settle(); /* start at rest, or it pours in on load */
  }

  /**
   * Two layers and a copy of the label. The pool paints behind the real
   * label; the copy sits on top in the on-liquid colour, clipped to the same
   * surface, so a letter changes colour exactly where the liquid crosses it
   * instead of sinking into it. That is not decoration — label and liquid are
   * often close in tone, and without it the words disappear as the level
   * rises. Setting --bw-liquid-ink to the button's own text colour turns the
   * flip off without turning the pool off.
   */
  Pool.prototype.mount = function () {
    var el = this.el;

    var ghost = document.createElement('span');
    ghost.className = 'bw-liquid__ghost';
    ghost.setAttribute('aria-hidden', 'true');
    for (var i = 0; i < el.childNodes.length; i++) {
      ghost.appendChild(el.childNodes[i].cloneNode(true));
    }

    var layer = document.createElement('span');
    layer.className = 'bw-liquid';
    layer.setAttribute('aria-hidden', 'true');

    var fill = document.createElement('span');
    fill.className = 'bw-liquid__fill';
    layer.appendChild(fill);

    /* Two droplets, reused. They are thrown by a fast upward pull and fall
       back under gravity; a pool that only ever sloshed reads as a gradient
       with a wobble rather than as a liquid. */
    this.drops = [];
    for (var d = 0; d < 2; d++) {
      var drop = document.createElement('span');
      drop.className = 'bw-liquid__drop';
      layer.appendChild(drop);
      this.drops.push({ el: drop, life: 0, x: 0, y: 0, vy: 0, r: 0 });
    }

    el.insertBefore(layer, el.firstChild);
    el.appendChild(ghost);

    this.layer = layer;
    this.fillEl = fill;
    this.ghost = ghost;
    el.classList.add('bw-liquid-on');
  };

  /**
   * Layout size, not getBoundingClientRect. The rect includes every ancestor
   * transform, and the reveal scales a button up from 70% as it enters the
   * viewport — the same moment the IntersectionObserver asks for a measure.
   * A pool sized to that shrunken rect left the fill sliced off along the
   * right and bottom, and stayed that way, because a transform settling does
   * not change the layout box and so never wakes the ResizeObserver. The
   * layer sits at inset:0 on the padding box, which is exactly what
   * clientWidth/clientHeight report, so this is also the closer match.
   */
  Pool.prototype.measure = function () {
    var w = this.el.clientWidth;
    var h = this.el.clientHeight;
    if (!w || !h) {
      var r = this.el.getBoundingClientRect();
      w = w || r.width || 1;
      h = h || r.height || 1;
    }
    if (this.h && h !== this.h) {
      /* Keep the pour where it was rather than restarting it: a resize that
         reset the surface would slam the level on every orientation change. */
      var k = h / this.h;
      for (var i = 0; i < COLS; i++) { this.y[i] *= k; this.v[i] *= k; }
    }
    this.w = w;
    this.h = h;
  };

  /**
   * The blend hero renders both blends and hides the inactive one with
   * visibility, not display — the two sets share one grid cell — so it still
   * has a box, still intersects the viewport, and would otherwise keep a pool
   * running inside a button nobody can see. visibility is the general test
   * for that, but reading a computed style is not something to do 60 times a
   * second: it is re-read only when something could actually have changed it.
   */
  Pool.prototype.recheck = function () {
    this.hidden = getComputedStyle(this.el).visibility === 'hidden';
  };

  Pool.prototype.settle = function () {
    var rest = this.h * this.level;
    for (var i = 0; i < COLS; i++) { this.y[i] = rest; this.v[i] = 0; }
  };

  /**
   * True while the surface is moving fast enough to be worth 60fps.
   *
   * Velocity, and deliberately not distance from the resting line: the drift
   * never stops, so a surface is permanently a few percent away from rest and
   * a distance test would call every button on the page busy for ever — which
   * is the throttle not existing. The threshold sits above what the drift
   * produces on its own (a few pixels a second) and well below a ripple, so
   * the tail of a settle drops to the idle rate about the point it stops
   * being visible.
   */
  Pool.prototype.busy = function () {
    var fast = this.h * 0.25;
    for (var i = 0; i < COLS; i++) {
      if (Math.abs(this.v[i]) > fast) return true;
    }
    return this.fillT > 0 || this.drops[0].life > 0 || this.drops[1].life > 0;
  };

  Pool.prototype.surge = function () {
    this.fillT = FILL_S;
    start();
  };

  Pool.prototype.step = function () {
    var h = this.h;
    var w = this.w;
    var rest = h * this.level;
    /* 0.069, up from 0.055: the pour read as heavy, especially on a phone
       where there is no hover to wake it, so the idle swell is a quarter
       larger and a quarter faster than it was. */
    var drift = h * 0.069 * this.amp;
    var i, target, d, g;

    this.t += STEP;

    /* Drift. Two travelling waves at 0.124Hz and 0.205Hz — the ratio is not a
       simple fraction, so the pair does not come back into phase within any
       time anyone spends looking at a button. */
    for (i = 0; i < COLS; i++) {
      target = rest + drift * (
        0.58 * Math.sin(this.t * 0.78 + i * 0.55 + this.p1) +
        0.42 * Math.sin(this.t * 1.29 - i * 0.31 + this.p2)
      );
      this.v[i] += (target - this.y[i]) * REST_K * STEP;
    }

    /* The press. Not a spring: a spring stiff enough to fill in a few frames
       overshoots and slaps. The surface is walked up to the brim by a fixed
       fraction of what is left each step, which is fast at first and soft at
       the top, and the spread pass below still ripples it on the way up. */
    if (this.fillT > 0) {
      this.fillT -= STEP;
      var brim = h * 0.97;
      for (i = 0; i < COLS; i++) {
        d = (brim - this.y[i]) * FILL_RATE;
        this.y[i] += d;
        this.v[i] = d / STEP * 0.35;
      }
    }

    /* The cursor. A gaussian centred on it, so the lift falls off smoothly
       rather than ending at a hard edge — an edge is what makes a pull read
       as a canned animation. Its width is a fifth of the button, roughly a
       fingertip's worth of surface. */
    if (this.hover) {
      var sigma = Math.max(w * 0.2, 26);
      var lift = clamp(h - this.py, h * 0.12, h * 0.94);
      for (i = 0; i < COLS; i++) {
        d = (i / (COLS - 1)) * w - this.px;
        g = Math.exp(-0.5 * (d * d) / (sigma * sigma));
        if (g < 0.01) continue;
        this.v[i] += (lift - this.y[i]) * g * PULL_K * STEP;
      }
    }

    /* Integrate, then let each column drag its neighbours. Spreading after
       the move is what turns 26 independent springs into one surface:
       displacement travels sideways and bounces off the ends. */
    var damp = Math.exp(-DAMP * STEP);
    for (i = 0; i < COLS; i++) {
      this.v[i] *= damp;
      this.y[i] += this.v[i] * STEP;
    }
    for (i = 0; i < COLS; i++) {
      var l = this.y[i > 0 ? i - 1 : 0];
      var r = this.y[i < COLS - 1 ? i + 1 : COLS - 1];
      this.v[i] += SPREAD * ((l - this.y[i]) + (r - this.y[i]));
      if (this.y[i] < 1) { this.y[i] = 1; this.v[i] *= -0.3; }
      if (this.y[i] > h - 1) { this.y[i] = h - 1; this.v[i] *= -0.3; }
    }

    this.stepDrops();
  };

  Pool.prototype.stepDrops = function () {
    var h = this.h;
    var w = this.w;
    var i, dr;

    /* Thrown only by a genuinely fast rise, so a slow hover stays calm. */
    if (this.hover) {
      for (i = 1; i < COLS - 1; i++) {
        if (this.v[i] < 210) continue;
        dr = this.drops[0].life <= 0 ? this.drops[0] : (this.drops[1].life <= 0 ? this.drops[1] : null);
        if (!dr) break;
        dr.x = (i / (COLS - 1)) * w + rand(-4, 4);
        dr.y = this.y[i];
        dr.vy = this.v[i] * rand(0.3, 0.5);
        dr.r = rand(2.5, 5);
        dr.life = 1;
      }
    }

    for (i = 0; i < 2; i++) {
      dr = this.drops[i];
      if (dr.life <= 0) continue;
      dr.vy -= 1400 * STEP; /* gravity, in button pixels */
      dr.y += dr.vy * STEP;
      var col = clamp(Math.round((dr.x / w) * (COLS - 1)), 0, COLS - 1);
      if (dr.y <= this.y[col] || dr.y > h * 1.4) dr.life = 0;
    }
  };

  /**
   * One polygon string, used by both the pool and the clipped label copy —
   * built once so the two can never disagree by a pixel. Coordinates run down
   * from the top of the button, which is what clip-path wants.
   */
  Pool.prototype.render = function () {
    var h = this.h;
    var w = this.w;
    var pts = '';
    var top = h;
    var i, x, y;

    for (i = 0; i < COLS; i++) {
      x = (i / (COLS - 1)) * w;
      y = h - this.y[i];
      if (y < top) top = y;
      pts += x.toFixed(1) + 'px ' + y.toFixed(1) + 'px,';
    }
    var poly = 'polygon(' + pts + w.toFixed(1) + 'px ' + h.toFixed(1) + 'px,0px ' + h.toFixed(1) + 'px)';
    this.fillEl.style.clipPath = poly;

    /* The label copy is only worth painting while the liquid is high enough
       to touch the type. Below that it is hidden — which is most of the time
       a page is sitting there drifting. 0.72 is under the deepest descender
       of a centred line, so the copy is already on by the time the waterline
       could clip a letter. */
    var lit = top < h * 0.72;
    if (lit) this.ghost.style.clipPath = poly;
    if (lit !== this.ghostLit) {
      this.ghost.style.visibility = lit ? 'visible' : 'hidden';
      this.ghostLit = lit;
    }

    for (i = 0; i < 2; i++) {
      var dr = this.drops[i];
      if (dr.life <= 0) {
        if (dr.el.style.opacity !== '0') dr.el.style.opacity = '0';
        continue;
      }
      dr.el.style.opacity = '1';
      dr.el.style.width = dr.el.style.height = (dr.r * 2).toFixed(1) + 'px';
      dr.el.style.transform = 'translate(' + (dr.x - dr.r).toFixed(1) + 'px,' +
        (h - dr.y - dr.r).toFixed(1) + 'px)';
    }
  };

  /* ---------------------------------------------------------------------- */

  function frame(now) {
    var dt = Math.min((now - last) / 1000, 0.25);
    last = now;

    var anyVisible = false;

    for (var i = 0; i < pools.length; i++) {
      var p = pools[i];
      if (!p.visible || p.hidden) continue;
      anyVisible = true;

      var hot = p.hover || p.busy();
      if (!hot && now - p.painted < IDLE_MS) {
        p.acc += dt; /* keep time moving so the drift does not stall */
        continue;
      }

      p.acc += dt;
      var steps = 0;
      while (p.acc >= STEP && steps < MAX_STEPS) {
        p.step();
        p.acc -= STEP;
        steps++;
      }
      if (p.acc > STEP) p.acc = 0;
      p.render();
      p.painted = now;
    }

    if (anyVisible) {
      requestAnimationFrame(frame);
    } else {
      running = false;
    }
  }

  function start() {
    if (running) return;
    running = true;
    last = performance.now();
    requestAnimationFrame(frame);
  }

  /* ---------------------------------------------------------------------- */

  function bindPointer(el, pool) {
    el.addEventListener('pointermove', function (e) {
      if (e.pointerType === 'touch') return;
      /* Map from the on-screen rect into the pool's layout pixels, so a
         button still scaling in under the reveal reads the cursor where it
         actually is on the surface. */
      var r = el.getBoundingClientRect();
      var sx = r.width ? pool.w / r.width : 1;
      var sy = r.height ? pool.h / r.height : 1;
      pool.px = (e.clientX - r.left) * sx;
      pool.py = (e.clientY - r.top) * sy;
      pool.hover = true;
    });
    el.addEventListener('pointerleave', function () { pool.hover = false; });
    el.addEventListener('pointercancel', function () { pool.hover = false; });

    /* Focus gets the same courtesy as hover, parked mid-button, so the
       keyboard path is not a dead button. */
    el.addEventListener('focus', function () {
      pool.px = pool.w * 0.5;
      pool.py = pool.h * 0.25;
      pool.hover = true;
    });
    el.addEventListener('blur', function () { pool.hover = false; });
  }

  /* Bound for every pointer type, unlike the hover above: a finger cannot
     hover, but it can press, and on a phone this is the only motion the
     button ever shows in response to the user. click covers the keyboard. */
  function bindPress(el, pool) {
    el.addEventListener('pointerdown', function () { pool.surge(); });
    el.addEventListener('click', function () { pool.surge(); });
  }

  var io = null;
  var ro = null;

  function mount() {
    var found = document.querySelectorAll('[data-bw-liquid]:not(.bw-liquid-on)');
    if (!found.length) return;

    if (!io) {
      io = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          var p = entries[i].target.__bwPool;
          if (!p) continue;
          p.visible = entries[i].isIntersecting;
          if (p.visible) {
            p.measure();
            p.recheck();
            start();
          }
        }
      }, { rootMargin: '80px' });

      ro = window.ResizeObserver ? new ResizeObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          var p = entries[i].target.__bwPool;
          if (p) {
            p.measure();
            p.render();
          }
        }
      }) : null;
    }

    for (var i = 0; i < found.length; i++) {
      var el = found[i];
      var pool = new Pool(el);
      el.__bwPool = pool;
      pools.push(pool);
      pool.render();

      if (reduce.matches) continue; /* poured, and that is all it will do */

      io.observe(el);
      if (ro) ro.observe(el);
      bindPress(el, pool);
      if (fine.matches) bindPointer(el, pool);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mount);
  } else {
    mount();
  }

  /* The theme editor re-renders a section into a fresh DOM, which leaves the
     old pools pointing at detached nodes. Drop those and mount the new ones. */
  document.addEventListener('shopify:section:load', function () {
    pools = pools.filter(function (p) { return p.el.isConnected; });
    mount();
  });

  /* Switching blend swaps which of the two hero sets is visible. Read after a
     frame, so the class and attribute changes driving it have landed. */
  document.addEventListener('bw:blendchange', function () {
    requestAnimationFrame(function () {
      for (var i = 0; i < pools.length; i++) {
        pools[i].recheck();
        if (!pools[i].hidden) pools[i].measure();
      }
      start();
    });
  });
}());
